import { Injectable, TRANSLATIONS }   from '@angular/core';
import { containsRect } from '@syncfusion/ej2-angular-charts';
import { Project, ProjectConfig, ProjectHolder } from '../models';
import { ConditionService } from './condition.service';

@Injectable()
export class ProjectService {
    
    private ATT_TYPE:string = "@type";
    private ATT_NAME:string = "@name";
    private ATT_PROJECT_CONFIG:string = "@project_config";
    private ATT_TEXT:string = "#text";
    private ATT_PARENT_ID:string = "@parent_ID";
    private ATT_CHILD_ID:string = "@child_ID";
    private ATT_ORDER:string = "@order";
    private ATT_ID:string = "@ID";

    private PTYPE_DELIVERABLE = "Deliverable";

    constructor(
        public conditionService: ConditionService
    ) { }

    private project_collection: ProjectHolder[] = [];

    public addProjectToCollection(project: Project): number {
        var index = this.project_collection.length;
        var config = project && project.project[this.ATT_PROJECT_CONFIG] ? JSON.parse(project.project[this.ATT_PROJECT_CONFIG]) : null;
        this.project_collection.push({ id: index, project: project, config: config });   // avoid parsing empty config
        return index;
    }

    private getProject(project_ID: number): Project {
        var ph = this.project_collection.find(p => p.id == project_ID);
        return ph ? ph.project : null;
    }

    private getProjectHolder(pj_or_id: number | Project): ProjectHolder {
        var ph = typeof pj_or_id === 'object' ? this.project_collection.find(p => p.project == pj_or_id) : this.project_collection.find(p => p.id == pj_or_id);
        return ph;
    }

    private getProjectConfig(pj_or_id: number | Project): ProjectConfig {
        var ph = this.getProjectHolder(pj_or_id);
        return ph ? ph.config : null;
    }

    public updateProject(project: Project, pj_id: number) {
        if (pj_id >= 0 && pj_id < this.project_collection.length) this.project_collection[pj_id].project = project;
    }

    private isDeliverableProject(pj_or_id: number | Project): boolean {
        var project = typeof pj_or_id === 'object' ? pj_or_id : this.getProject(pj_or_id);
        //var project = Number.isInteger(pj_or_id) ? this.getProject(pj_or_id) pj_or_id : ;
        var project_element = project ? project.project : null;
        return (project_element && project_element[this.ATT_TYPE] && project_element[this.ATT_TYPE] == this.PTYPE_DELIVERABLE);
    }

    public getAvailableDeliverables(pj_or_id: number | Project): any[] {
        var project = typeof pj_or_id === 'object' ? pj_or_id : this.getProject(pj_or_id);
        var deliverables = project.project.deliverable;
        if (this.isDeliverableProject(pj_or_id) && project.project.deliverable && project.project.deliverable.length == null) {
            deliverables = [ project.project.deliverable ];
        }
        return deliverables
    }

    public isDeliverableVisible(pj_or_id: number | Project, deliverable: any) {
        return this.isElementVisible(pj_or_id, deliverable);
    }

    public isElementVisible(pj_or_id: number | Project, element: any) {
        var bVisible = true;
        var project = typeof pj_or_id === 'object' ? pj_or_id : this.getProject(pj_or_id);
        if (element && element.additional_fields && element.additional_fields.field) {
            var conditions_field = element.additional_fields.field.find(f => f[this.ATT_NAME] == "Conditions");
            var conditions = conditions_field[this.ATT_TEXT] ? JSON.parse(conditions_field[this.ATT_TEXT]) : null;
            if (conditions && conditions.sn_visible) {
                var vis_condition = this.getProjectCondition(pj_or_id, conditions.sn_visible);
                //TODO: evaluate condition
            }
        }
        return bVisible;
    }    

    public getProjectCondition(pj_or_id: number | Project, condition_name: string) {
        var config = this.getProjectConfig(pj_or_id);
        if (config && config.ConditionCollections) {
            return config.ConditionCollections.find(c => c.name == condition_name);
        }
        return null;        
    }

    private getRelatedElementsByRelation(relations, parent, children) {
        var p_c_relations = [];
        relations.forEach(r => {
            if (parent[this.ATT_ID] == r[this.ATT_PARENT_ID]) {
                p_c_relations.push(r);
            }
        })

        // sort relations as ascending
        var p_order = this.ATT_ORDER;
        p_c_relations.sort(function(a, b) {
            return a[p_order] - b[p_order];
        })

        var elements = []
        // add phase to project 
        children.forEach(ph => {
            var r = p_c_relations.find(r => r[this.ATT_CHILD_ID] == ph[this.ATT_ID]);
            if (r) elements.push(ph);
        })
        return elements;
    }

    public convertToDeliverableProject(dp: Project) {
        if (dp.project[this.ATT_TYPE] == this.PTYPE_DELIVERABLE) {
            var project = JSON.parse(JSON.stringify(dp.project));
            var relations = project.relation;
            var phases = Array.isArray(project.phase) ? project.phase : [ project.phase ];
            var containers = project.containers.container;
            var deliverables = project.deliverable;

            // get phase relations
            var pj_ph_relations = [];
            project.phases = [];
            relations.forEach(r => {
            // get project phase relations
                if (project[this.ATT_ID] == r[this.ATT_PARENT_ID]) {
                    pj_ph_relations.push(r);
                }
            })
            // sort relations as ascending
            pj_ph_relations.sort(function(a, b) {
                return a[this.ATT_ORDER] - b[this.ATT_ORDER];
            })
            // add phase to project 
            if (phases)
            phases.forEach(ph => {
                var r = pj_ph_relations.find(r => r[this.ATT_CHILD_ID] == ph[this.ATT_ID]);
                if (r) project.phases.push(ph);
            })

            project.phases = this.getRelatedElementsByRelation(relations, project, phases);
            project.phases.forEach(ph => {
                ph.categories = this.getRelatedElementsByRelation(relations, ph, containers);
                ph.categories.forEach(cat => {
                    cat.deliverables = this.getRelatedElementsByRelation(relations, cat, deliverables);
                })                
            });

            // convert and remove all xml properties
            project = this.removeXmlProperties(project);
            project.phases.forEach(ph => { 
                ph = this.removeXmlProperties(ph); 
                ph.categories.forEach(cat => {
                    cat = this.removeXmlProperties(cat);
                    cat.deliverables.forEach(d => {
                        d = this.removeXmlProperties(d);
                    })
                })
            });

            // delete project properties that are not needed now
            delete project.phase;
            delete project.deliverable;
            delete project.relation;
        }
        return project;
    } 

    private removeXmlProperties(element:any): any {
        var properties = [];
        for (const p in element) {
            if (p.indexOf('@') >= 0) properties.push({ prop: p, name: p.replace('@', '') });
        }

        properties.forEach(p => {
            element[p.name] = element[p.prop];
            delete element[p.prop];
        })

        return element;
    }
}


// WEBPACK FOOTER //
// C:/Azure/Source/Workspaces/SalesNOW/SalesSOLO/WebSites/SalesSOLO.Web.Angular.telesales/src/app/services/project.service.ts